/**
 * Support page content — 100% fidelity to content/company/support.md
 * Cross-reference: content/company/support.md
 * 18 sections, all handcrafted from source.
 */

export const supportMeta = {
  title: "Support | Perception X2 Help Center",
  description:
    "Perception X2 help center and documentation. 24/7 support, technical guides, API references, and dedicated account management for all platform tiers.",
  slug: "support",
  canonical: "https://perception.ac/support",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  supportIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Support",
  title: "Your Dedicated",
  titleAccent: "Support Command Center",
  lede: "Perception X2 delivers 24/7/365 support through dedicated account teams, comprehensive documentation, and direct engineering access. Every client receives the support infrastructure their operational requirements demand.",
  body: "From onboarding through deployment and optimization, our support ecosystem ensures you extract maximum value from every Perception X2 capability. No ticket queues. No generic responses. Direct access to the people who built the platform.",
  cta: [
    { label: "Access Documentation", href: "#documentation-hub" },
    { label: "Contact Support", href: "#contact-support" },
  ],
  supportIndicators: [
    "24/7/365 Dedicated Support",
    "Direct Engineering Access",
    "Sub-1-Hour Response (Enterprise)",
    "99.9999% Platform Uptime",
  ],
};

export interface ExecSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const executiveSummary: ExecSummaryData = {
  eyebrow: "Executive Summary",
  title: "World-Class Support for Mission-Critical Operations",
  intro: "Perception X2 serves the world's most demanding organizations — from government agencies to Fortune 100 enterprises to high-net-worth individuals. Our support infrastructure is built to match the operational intensity of our client base.",
  pillars: [
    { title: "Support Philosophy", body: "We do not operate ticket queues. Every client engagement is assigned a dedicated support team with direct access to engineering resources. When you need assistance, you speak with people who understand your deployment, your objectives, and your operational environment." },
    { title: "Comprehensive Coverage", body: "Our support ecosystem spans documentation, training, technical assistance, and strategic guidance. Whether you need help configuring an integration, optimizing campaign performance, or troubleshooting a technical issue, Perception X2 support delivers resolution with the urgency your operations require." },
    { title: "Proven Track Record", body: "Across 15+ years of operations serving 300+ elite clients globally, our support infrastructure has maintained a 100% client satisfaction rating. We succeed when you succeed — and our support model reflects that commitment." },
  ],
  cta: [
    { label: "Explore Documentation", href: "#documentation-hub" },
    { label: "Meet Your Support Team", href: "#dedicated-account-management" },
  ],
};

export interface TiersData {
  eyebrow: string;
  title: string;
  intro: string;
  rows: { feature: string; standard: string; professional: string; enterprise: string }[];
  guidance: { tier: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const supportTiers: TiersData = {
  eyebrow: "Support Tiers",
  title: "Tailored Support for Every Engagement Level",
  intro: "Perception X2 offers three support tiers designed to match the complexity and urgency of your operational requirements. Each tier delivers comprehensive coverage with escalation paths calibrated to your needs.",
  rows: [
    { feature: "Response Time", standard: "< 8 hours", professional: "< 4 hours", enterprise: "< 1 hour" },
    { feature: "Support Hours", standard: "Business hours", professional: "24/5", enterprise: "24/7/365" },
    { feature: "Dedicated Account Manager", standard: "No", professional: "Yes", enterprise: "Yes" },
    { feature: "Dedicated Engineering Access", standard: "No", professional: "Shared", enterprise: "Dedicated" },
    { feature: "Onboarding Support", standard: "Self-guided", professional: "Guided", enterprise: "Full white-glove" },
    { feature: "Training Sessions", standard: "2 included", professional: "5 included", enterprise: "Unlimited" },
    { feature: "Quarterly Business Reviews", standard: "No", professional: "Yes", enterprise: "Yes" },
    { feature: "Custom Integration Support", standard: "Limited", professional: "Yes", enterprise: "Yes" },
    { feature: "Priority Bug Resolution", standard: "No", professional: "Yes", enterprise: "Yes" },
    { feature: "Direct Slack/Teams Channel", standard: "No", professional: "No", enterprise: "Yes" },
  ],
  guidance: [
    { tier: "Standard", body: "suits organizations with established technical teams and straightforward deployment requirements" },
    { tier: "Professional", body: "serves mid-market and enterprise clients requiring dedicated guidance and faster response windows" },
    { tier: "Enterprise", body: "delivers the full Perception X2 support infrastructure — direct engineering access, white-glove onboarding, and around-the-clock coverage" },
  ],
  cta: [
    { label: "Review Support Tiers", href: "#support-tiers" },
    { label: "Contact Sales", href: "/contact" },
  ],
};

export interface DocData {
  eyebrow: string;
  title: string;
  intro: string;
  categories: { category: string; content: string; audience: string }[];
  features: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const documentationHub: DocData = {
  eyebrow: "Documentation Hub",
  title: "Comprehensive Technical Documentation",
  intro: "Perception X2 maintains a complete documentation library covering every platform capability, integration, and API endpoint. Our documentation is written by engineers, reviewed by technical writers, and updated with every platform release.",
  categories: [
    { category: "Platform Guides", content: "Core platform usage, configuration, and optimization", audience: "All users" },
    { category: "API Reference", content: "Complete REST, WebSocket, and gRPC documentation", audience: "Developers" },
    { category: "Integration Guides", content: "Step-by-step connection guides for 50+ platforms", audience: "Technical teams" },
    { category: "SDK & Libraries", content: "Code resources for Python, Go, JavaScript, and Rust", audience: "Developers" },
    { category: "Webhook Documentation", content: "Event subscriptions, payload schemas, and retry logic", audience: "Developers" },
    { category: "Onboarding Playbooks", content: "Deployment checklists, configuration templates, and best practices", audience: "Operations" },
    { category: "Security Documentation", content: "Architecture overview, compliance certifications, and audit procedures", audience: "Security teams" },
    { category: "Release Notes", content: "Changelog, migration guides, and deprecation notices", audience: "All users" },
  ],
  features: [
    "Searchable knowledge base with full-text search",
    "Code examples in multiple programming languages",
    "Interactive API explorer for live testing",
    "Version-specific documentation (current and legacy)",
    "Downloadable PDF exports for offline access",
    "Regular updates aligned with platform releases",
  ],
  cta: [
    { label: "Access Documentation", href: "#documentation-hub" },
    { label: "View API Reference", href: "#api-support" },
  ],
};

export interface OnboardingData {
  eyebrow: string;
  title: string;
  intro: string;
  phases: { phase: string; duration: string; activities: string; deliverable: string }[];
  resources: string[];
  transition: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const onboardingSupport: OnboardingData = {
  eyebrow: "Onboarding Support",
  title: "Structured Onboarding for Rapid Time-to-Value",
  intro: "Every Perception X2 engagement begins with a structured onboarding process designed to accelerate your team's proficiency and ensure optimal platform configuration from day one.",
  phases: [
    { phase: "Discovery", duration: "Days 1-3", activities: "Requirements gathering, objective alignment, environment assessment", deliverable: "Onboarding roadmap" },
    { phase: "Configuration", duration: "Days 4-10", activities: "Platform setup, integration connections, security configuration", deliverable: "Configured environment" },
    { phase: "Training", duration: "Days 11-17", activities: "Team training sessions, hands-on workshops, documentation walkthrough", deliverable: "Trained team" },
    { phase: "Validation", duration: "Days 18-21", activities: "Testing, performance validation, go-live preparation", deliverable: "Validated deployment" },
    { phase: "Go-Live", duration: "Day 22+", activities: "Launch support, monitoring, optimization recommendations", deliverable: "Live deployment" },
  ],
  resources: [
    "Dedicated onboarding manager for Professional and Enterprise tiers",
    "Self-guided onboarding portal with step-by-step tutorials",
    "Video walkthroughs for every major platform feature",
    "Configuration templates for common deployment patterns",
    "Integration testing tools and validation checklists",
    "Direct Slack/Teams channel with your onboarding team (Enterprise)",
  ],
  transition: {
    title: "Post-Onboarding Transition",
    body: "Upon completion of onboarding, your engagement seamlessly transitions to your dedicated support team. No gaps. No re-explanation of context. Your support team receives full onboarding documentation and maintains continuity through every phase of your engagement.",
  },
  cta: [
    { label: "Start Onboarding", href: "#onboarding-support" },
    { label: "View Onboarding Playbook", href: "#documentation-hub" },
  ],
};

export interface TechSupportData {
  eyebrow: string;
  title: string;
  intro: string;
  channels: { channel: string; availability: string; response: string; bestFor: string }[];
  scope: string[];
  escalation: { level: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const technicalSupport: TechSupportData = {
  eyebrow: "Technical Support",
  title: "Direct Access to the Engineers Who Built the Platform",
  intro: "Technical support at Perception X2 bypasses traditional ticket queues. Your support team includes platform engineers with deep knowledge of every system component — from the Organic Simulation Engine to the Autonomous Operations Engine.",
  channels: [
    { channel: "Dedicated Slack/Teams", availability: "Enterprise only", response: "Minutes", bestFor: "Real-time collaboration" },
    { channel: "Priority Email", availability: "Professional +", response: "< 4 hours", bestFor: "Detailed technical inquiries" },
    { channel: "Support Portal", availability: "All tiers", response: "< 8 hours", bestFor: "Standard requests" },
    { channel: "Emergency Hotline", availability: "Enterprise only", response: "Immediate", bestFor: "Critical system issues" },
    { channel: "Screen Share Sessions", availability: "Professional +", response: "Scheduled", bestFor: "Complex troubleshooting" },
  ],
  scope: [
    "Platform configuration and optimization",
    "Integration troubleshooting and debugging",
    "API usage guidance and code review",
    "Performance analysis and tuning recommendations",
    "Security configuration and compliance guidance",
    "Custom deployment architecture consultation",
    "Data pipeline configuration and validation",
    "AI engine tuning and calibration support",
  ],
  escalation: [
    { level: "Level 1", title: "Standard triage", body: "Support team triages and resolves standard issues (< 2 hours)" },
    { level: "Level 2", title: "Senior engineering", body: "Senior engineers investigate complex technical problems (< 8 hours)" },
    { level: "Level 3", title: "Engineering leadership", body: "Engineering leadership addresses architecture-level concerns (< 24 hours)" },
    { level: "Level 4", title: "Executive escalation", body: "Executive escalation for critical operational impacts (immediate)" },
  ],
  cta: [
    { label: "Contact Technical Support", href: "#contact-support" },
    { label: "Review Escalation Protocol", href: "#support-escalation" },
  ],
};

export interface ApiSupportData {
  eyebrow: string;
  title: string;
  intro: string;
  resources: { resource: string; description: string; access: string }[];
  channels: { channel: string; availability: string; response: string }[];
  apiResources: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const apiSupport: ApiSupportData = {
  eyebrow: "API Support",
  title: "Developer-First API Assistance",
  intro: "Perception X2 provides comprehensive API support for teams building custom integrations, extending platform capabilities, or embedding Perception X2 intelligence into existing workflows.",
  resources: [
    { resource: "REST API Reference", description: "Complete endpoint documentation with examples", access: "Open" },
    { resource: "WebSocket API", description: "Real-time event streams and subscriptions", access: "Open" },
    { resource: "gRPC Services", description: "High-performance binary protocol documentation", access: "Open" },
    { resource: "SDK Documentation", description: "Language-specific libraries and usage guides", access: "Open" },
    { resource: "Webhook Reference", description: "Event subscriptions and payload schemas", access: "Open" },
    { resource: "Rate Limiting Guide", description: "Quota management and optimization strategies", access: "Open" },
  ],
  channels: [
    { channel: "Developer Slack Community", availability: "All API users", response: "Peer + staff support" },
    { channel: "GitHub Issues", availability: "All API users", response: "< 24 hours" },
    { channel: "Dedicated Engineer", availability: "Enterprise only", response: "< 4 hours" },
    { channel: "Office Hours", availability: "All API users", response: "Weekly scheduled sessions" },
  ],
  apiResources: [
    "Interactive API explorer with live request/response testing",
    "Postman collections for rapid prototyping",
    "Code samples in Python, Go, JavaScript, and Rust",
    "Integration templates for common deployment patterns",
    "Webhook testing sandbox for event validation",
    "SDK auto-generation for custom language support",
  ],
  cta: [
    { label: "Access API Documentation", href: "#api-support" },
    { label: "Join Developer Community", href: "#community-and-forums" },
  ],
};

export interface TrainingData {
  eyebrow: string;
  title: string;
  intro: string;
  programs: { program: string; duration: string; audience: string; format: string }[];
  resources: string[];
  certifications: { name: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const trainingEducation: TrainingData = {
  eyebrow: "Training and Education",
  title: "Build Internal Expertise Across Your Organization",
  intro: "Perception X2 offers structured training programs designed to build platform proficiency across your team. From foundational usage to advanced optimization, our training resources ensure your organization extracts maximum value from every capability.",
  programs: [
    { program: "Platform Fundamentals", duration: "4 hours", audience: "All users", format: "Self-paced or live" },
    { program: "Advanced Operations", duration: "8 hours", audience: "Power users", format: "Live workshop" },
    { program: "Developer Deep Dive", duration: "12 hours", audience: "Engineering teams", format: "Live workshop" },
    { program: "Administrator Certification", duration: "16 hours", audience: "Platform admins", format: "Live + exam" },
    { program: "Executive Briefing", duration: "2 hours", audience: "Leadership", format: "Live presentation" },
  ],
  resources: [
    "Self-paced learning portal with video modules",
    "Hands-on lab environments for practice deployments",
    "Certification programs for platform administrators",
    "Quarterly advanced training sessions for existing clients",
    "Custom training curricula for Enterprise deployments",
    "Recorded sessions available on-demand through the documentation hub",
  ],
  certifications: [
    { name: "Certified Operator", body: "Platform fundamentals and daily operations" },
    { name: "Certified Administrator", body: "Configuration, security, and user management" },
    { name: "Certified Developer", body: "API integration, SDK usage, and custom deployments" },
    { name: "Certified Strategist", body: "Campaign optimization, analytics interpretation, and performance tuning" },
  ],
  cta: [
    { label: "Explore Training Programs", href: "#training-and-education" },
    { label: "Schedule Training", href: "#contact-support" },
  ],
};

export interface AccountData {
  eyebrow: string;
  title: string;
  intro: string;
  responsibilities: { responsibility: string; frequency: string; format: string }[];
  access: string[];
  enterpriseTeam: { role: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const dedicatedAccount: AccountData = {
  eyebrow: "Dedicated Account Management",
  title: "Your Single Point of Strategic Contact",
  intro: "Professional and Enterprise tier clients receive a dedicated account manager who serves as your primary strategic contact across all Perception X2 engagements. Your account manager understands your deployment, your objectives, and your operational history.",
  responsibilities: [
    { responsibility: "Strategic reviews", frequency: "Monthly", format: "Video call" },
    { responsibility: "Performance analysis", frequency: "Bi-weekly", format: "Report + call" },
    { responsibility: "Platform optimization", frequency: "Ongoing", format: "As needed" },
    { responsibility: "Escalation management", frequency: "As needed", format: "Direct contact" },
    { responsibility: "Roadmap alignment", frequency: "Quarterly", format: "Video call" },
    { responsibility: "Business reviews", frequency: "Quarterly", format: "Presentation" },
  ],
  access: [
    "Direct phone and email availability during business hours",
    "Priority escalation for any support concern",
    "Proactive platform health monitoring and recommendations",
    "Coordination with engineering for custom requirements",
    "Executive briefing facilitation for leadership stakeholders",
    "Onboarding and training coordination for new team members",
  ],
  enterpriseTeam: [
    { role: "Dedicated Account Manager", body: "Strategic relationship" },
    { role: "Technical Account Manager", body: "Platform optimization" },
    { role: "Solutions Engineer", body: "Custom integration support" },
    { role: "Customer Success Manager", body: "Adoption and value realization" },
  ],
  cta: [
    { label: "Dedicated Account Management", href: "#dedicated-account-management" },
    { label: "Request Account Team", href: "#contact-support" },
  ],
};

export interface KnowledgeData {
  eyebrow: string;
  title: string;
  intro: string;
  categories: { category: string; articles: string; updates: string }[];
  features: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const knowledgeBase: KnowledgeData = {
  eyebrow: "Knowledge Base",
  title: "Self-Service Answers at Your Fingertips",
  intro: "The Perception X2 knowledge base contains hundreds of articles, tutorials, and troubleshooting guides organized by topic, feature, and use case. Our knowledge base is continuously updated with new content based on client questions and platform releases.",
  categories: [
    { category: "Getting Started", articles: "45+", updates: "Monthly" },
    { category: "Platform Configuration", articles: "120+", updates: "Bi-weekly" },
    { category: "API & Integration", articles: "85+", updates: "Weekly" },
    { category: "Analytics & Reporting", articles: "60+", updates: "Monthly" },
    { category: "Security & Compliance", articles: "40+", updates: "Monthly" },
    { category: "Troubleshooting", articles: "150+", updates: "Weekly" },
    { category: "Best Practices", articles: "75+", updates: "Monthly" },
    { category: "Release Notes", articles: "Ongoing", updates: "Per release" },
  ],
  features: [
    "Full-text search with relevance ranking",
    "Topic-based navigation and filtering",
    "Code snippets and configuration examples",
    "Visual guides with annotated screenshots",
    "Video tutorials embedded in relevant articles",
    "Community-contributed solutions and tips",
    "Article feedback and rating system",
    "Regular content audits for accuracy and relevance",
  ],
  cta: [
    { label: "Search Knowledge Base", href: "#knowledge-base" },
    { label: "Browse by Topic", href: "#documentation-hub" },
  ],
};

export interface EscalationData {
  eyebrow: string;
  title: string;
  intro: string;
  matrix: { severity: string; definition: string; response: string; resolution: string; communication: string }[];
  process: { step: string; title: string; body: string }[];
  contacts: { level: string; trigger: string; contact: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const supportEscalation: EscalationData = {
  eyebrow: "Support Escalation",
  title: "Structured Resolution for Every Issue",
  intro: "Perception X2 maintains a rigorous escalation protocol ensuring every support inquiry receives appropriate attention and resolution within defined timelines.",
  matrix: [
    { severity: "Critical", definition: "System outage or data loss", response: "< 15 minutes", resolution: "< 4 hours", communication: "Real-time updates" },
    { severity: "High", definition: "Major feature degradation", response: "< 1 hour", resolution: "< 8 hours", communication: "Hourly updates" },
    { severity: "Medium", definition: "Limited impact, workaround available", response: "< 4 hours", resolution: "< 24 hours", communication: "Daily updates" },
    { severity: "Low", definition: "General inquiry or enhancement request", response: "< 8 hours", resolution: "< 72 hours", communication: "Status updates" },
  ],
  process: [
    { step: "1", title: "Initial Contact", body: "Submit through your preferred support channel" },
    { step: "2", title: "Triage", body: "Support team assesses severity and assigns resources" },
    { step: "3", title: "Investigation", body: "Engineering team diagnoses root cause" },
    { step: "4", title: "Resolution", body: "Fix deployed and validated" },
    { step: "5", title: "Communication", body: "Status updates provided throughout" },
    { step: "6", title: "Post-Resolution", body: "Root cause analysis and prevention recommendations" },
  ],
  contacts: [
    { level: "Level 1", trigger: "Standard support inquiry", contact: "Support team" },
    { level: "Level 2", trigger: "No resolution within SLA", contact: "Support manager" },
    { level: "Level 3", trigger: "Critical business impact", contact: "Account manager + engineering lead" },
    { level: "Level 4", trigger: "Executive escalation", contact: "Executive team" },
  ],
  cta: [
    { label: "Review Escalation Protocol", href: "#support-escalation" },
    { label: "Contact Support", href: "#contact-support" },
  ],
};

export interface StatusIncidentsData {
  eyebrow: string;
  title: string;
  intro: string;
  features: string[];
  communication: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const platformStatusIncidents: StatusIncidentsData = {
  eyebrow: "Platform Status and Incidents",
  title: "Real-Time Visibility Into Platform Health",
  intro: "Perception X2 maintains a public status page providing real-time transparency into platform health, incident history, and scheduled maintenance. Our 99.9999% uptime SLA is backed by comprehensive monitoring across all services.",
  features: [
    "Real-time service health indicators for all platform components",
    "Historical uptime data with monthly and annual summaries",
    "Active incident reports with timeline and impact assessment",
    "Scheduled maintenance notifications with advance notice",
    "API endpoint health monitoring",
    "Regional performance metrics across all deployment zones",
  ],
  communication: [
    "Status page updated within 10 minutes of detection",
    "Email notifications sent to affected clients within 15 minutes",
    "Real-time updates every 5 minutes for Critical incidents",
    "Post-incident summary published within 24 hours",
    "Root cause analysis completed within 48 hours",
  ],
  cta: [
    { label: "View Platform Status", href: "/status" },
    { label: "Subscribe to Status Updates", href: "#contact-support" },
  ],
};

export interface CommunityData {
  eyebrow: string;
  title: string;
  intro: string;
  channels: { channel: string; purpose: string; access: string }[];
  resources: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const communityForums: CommunityData = {
  eyebrow: "Community and Forums",
  title: "Connect With Peers and Experts",
  intro: "The Perception X2 developer community provides a space for technical discussions, best practice sharing, and peer-to-peer support. Our community is moderated by Perception X2 engineers and technical writers.",
  channels: [
    { channel: "Developer Forum", purpose: "Technical discussions and Q&A", access: "All API users" },
    { channel: "Slack Community", purpose: "Real-time collaboration", access: "All clients" },
    { channel: "GitHub Discussions", purpose: "Feature requests and roadmap input", access: "All users" },
    { channel: "Office Hours", purpose: "Live Q&A with engineering", access: "All API users" },
    { channel: "Annual Summit", purpose: "In-person networking and training", access: "All clients" },
  ],
  resources: [
    "Peer-contributed integration examples and templates",
    "Best practice threads organized by industry and use case",
    "Feature request voting and roadmap visibility",
    "Direct responses from Perception X2 engineering team",
    "Monthly community newsletter with tips and updates",
    "Annual client summit with training and networking",
  ],
  cta: [
    { label: "Join Community", href: "#community-and-forums" },
    { label: "Attend Office Hours", href: "#api-support" },
  ],
};

export interface SecComplData {
  eyebrow: string;
  title: string;
  intro: string;
  services: { service: string; description: string; availability: string }[];
  certifications: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const securityCompliance: SecComplData = {
  eyebrow: "Security and Compliance Support",
  title: "Dedicated Support for Security-Critical Environments",
  intro: "Perception X2 provides specialized support for clients operating in security-sensitive environments. Our security support team includes certified professionals with expertise in compliance frameworks, threat modeling, and secure architecture design.",
  services: [
    { service: "Security Architecture Review", description: "Platform security configuration validation", availability: "Enterprise" },
    { service: "Compliance Documentation", description: "SOC 2, ISO 27001, and custom compliance artifacts", availability: "All tiers" },
    { service: "Penetration Testing Coordination", description: "Third-party testing facilitation and remediation", availability: "Enterprise" },
    { service: "Incident Response Support", description: "Security incident investigation and remediation", availability: "Enterprise" },
    { service: "Audit Support", description: "Compliance audit preparation and evidence gathering", availability: "Professional +" },
    { service: "Security Training", description: "Platform security best practices for your team", availability: "All tiers" },
  ],
  certifications: [
    "SOC 2 Type II — Independently audited annually",
    "ISO 27001 — Certified information security management",
    "GDPR — Full compliance with data protection requirements",
    "CCPA — California consumer privacy compliance",
    "Custom compliance frameworks available for Enterprise clients",
  ],
  cta: [
    { label: "Security Support", href: "#security-and-compliance-support" },
    { label: "Request Compliance Documentation", href: "#contact-support" },
  ],
};

export interface FeedbackData {
  eyebrow: string;
  title: string;
  intro: string;
  channels: { channel: string; purpose: string; response: string }[];
  process: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const feedbackImprovement: FeedbackData = {
  eyebrow: "Feedback and Improvement",
  title: "Your Input Shapes the Platform",
  intro: "Perception X2 actively solicits client feedback to guide platform development and support improvement. Every piece of feedback is reviewed by product and engineering teams and incorporated into our development roadmap where appropriate.",
  channels: [
    { channel: "Feature Request Portal", purpose: "Submit and vote on feature requests", response: "Acknowledgment within 48 hours" },
    { channel: "NPS Surveys", purpose: "Quarterly satisfaction measurement", response: "Reviewed within 7 days" },
    { channel: "Account Reviews", purpose: "Strategic feedback during business reviews", response: "Addressed in next review" },
    { channel: "Support Surveys", purpose: "Post-interaction feedback", response: "Reviewed within 24 hours" },
    { channel: "Executive Feedback", purpose: "Direct leadership input", response: "Acknowledgment within 24 hours" },
  ],
  process: [
    "Feature requests are triaged weekly by the product team",
    "High-vote features are prioritized in quarterly planning",
    "NPS trends are reviewed by executive leadership monthly",
    "Support satisfaction data drives process improvements",
    "Client advisory board provides strategic direction input",
  ],
  cta: [
    { label: "Submit Feedback", href: "#feedback-and-improvement" },
    { label: "View Product Roadmap", href: "#documentation-hub" },
  ],
};

export interface BillingData {
  eyebrow: string;
  title: string;
  intro: string;
  services: { service: string; channel: string; response: string }[];
  selfService: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const billingAccount: BillingData = {
  eyebrow: "Billing and Account Support",
  title: "Streamlined Account Management",
  intro: "Perception X2 provides dedicated support for billing inquiries, account modifications, and subscription management. Our billing support team resolves account questions with the same urgency we apply to technical issues.",
  services: [
    { service: "Invoice inquiries", channel: "Email", response: "< 24 hours" },
    { service: "Payment processing", channel: "Support portal", response: "< 4 hours" },
    { service: "Subscription changes", channel: "Account manager", response: "Same business day" },
    { service: "Tier upgrades", channel: "Account manager", response: "Immediate" },
    { service: "Tax documentation", channel: "Email", response: "< 48 hours" },
    { service: "Contract renewal", channel: "Account manager", response: "30-day advance notice" },
  ],
  selfService: [
    "Online billing portal for invoice history and payment management",
    "Subscription dashboard for tier and add-on management",
    "Usage analytics for monitoring platform consumption",
    "Export tools for financial reporting and reconciliation",
    "Automated payment processing with multiple method support",
  ],
  cta: [
    { label: "Billing Support", href: "#billing-and-account-support" },
    { label: "Access Billing Portal", href: "#contact-support" },
  ],
};

export interface ContactData {
  eyebrow: string;
  title: string;
  intro: string;
  channels: { channel: string; availability: string; bestFor: string }[];
  contacts: { purpose: string; contact: string }[];
  commitments: { tier: string; response: string; coverage: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const contactSupport: ContactData = {
  eyebrow: "Contact Support",
  title: "Reach Us Through Your Preferred Channel",
  intro: "Perception X2 support is available through multiple channels, ensuring you can reach the right team for your inquiry with minimal friction.",
  channels: [
    { channel: "Support Portal", availability: "24/7 submission, business hours response", bestFor: "Standard inquiries" },
    { channel: "Email", availability: "24/7 submission", bestFor: "Detailed technical questions" },
    { channel: "Phone", availability: "Enterprise 24/7, Professional business hours", bestFor: "Urgent issues" },
    { channel: "Dedicated Slack/Teams", availability: "Enterprise only", bestFor: "Real-time collaboration" },
    { channel: "Emergency Hotline", availability: "Enterprise only", bestFor: "Critical system issues" },
    { channel: "Account Manager", availability: "Business hours", bestFor: "Strategic discussions" },
  ],
  contacts: [
    { purpose: "General Support", contact: "support@perception.ac" },
    { purpose: "Technical Inquiries", contact: "technical@perception.ac" },
    { purpose: "Security Concerns", contact: "security@perception.ac" },
    { purpose: "Billing Questions", contact: "billing@perception.ac" },
    { purpose: "Sales Inquiries", contact: "sales@perception.ac" },
    { purpose: "Press & Media", contact: "press@perception.ac" },
    { purpose: "Partnership Opportunities", contact: "partners@perception.ac" },
  ],
  commitments: [
    { tier: "Enterprise", response: "< 1 hour response on all channels", coverage: "24/7/365" },
    { tier: "Professional", response: "< 4 hours response", coverage: "24/5 coverage" },
    { tier: "Standard", response: "< 8 hours response", coverage: "Business hours coverage" },
  ],
  cta: [
    { label: "Contact Support", href: "#contact-support" },
    { label: "Request a Consultation", href: "/contact" },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  body: string;
  support: string[];
  nextSteps: string[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const supportCta: CtaData = {
  eyebrow: "Get Started",
  title: "Experience the Perception X2 Difference",
  intro: "Every Perception X2 client receives support infrastructure that matches the intensity of their operational requirements. From onboarding through optimization, our dedicated teams ensure you extract maximum value from every platform capability.",
  body: "Your perception is being shaped right now — by you or by others. Our support team is ready to help you command that perception with precision.",
  support: [
    "Documentation Hub — Comprehensive guides, API references, and tutorials",
    "Knowledge Base — Self-service answers for common questions",
    "Direct Support — Dedicated teams for Professional and Enterprise clients",
    "Community — Connect with peers and Perception X2 engineers",
  ],
  nextSteps: [
    "Explore our documentation library for platform guidance",
    "Contact our support team for personalized assistance",
    "Request a demo to experience Perception X2 capabilities firsthand",
  ],
  cta: [
    { label: "Contact Support", href: "#contact-support" },
    { label: "Request a Demo", href: "/demo" },
    { label: "Access Documentation", href: "#documentation-hub" },
  ],
};
